import { BlockEmbed } from 'quill/blots/block';
import Quill from 'quill/core';
import type { Range } from 'quill/core';
import type QuillEditor from './QuillEditor';
import DraggingAutoScroll from './utils/DraggingAutoScroll';
import findClosestScrollingContainer from './utils/findClosestScrollingContainer';

// Triple-click selects the entire paragraph.
//
// Note: We override the default behavior due to inconsistent browser behavior.
// Examples:
// 1. If a mention is within a paragraph, a triple-click only selects up to the mention,
//    not the entire paragraph.
// 2. Sometimes triple-clicks don't select anything:
//    - https://bugs.webkit.org/show_bug.cgi?id=48037
//    - https://bugs.chromium.org/p/chromium/issues/detail?id=1371268
const selectParagraphsWithTripleClick = (quill: QuillEditor) => {
  const range = quill.getSelection();
  if (!range) return false;

  const [line, offset] = quill.getLine(range.index);
  if (!line) return false;

  const start = range.index - offset;
  const end = start + line.length() - 1;

  quill.setSelection({ index: start, length: end - start }, Quill.sources.USER);

  // Holding down the mouse button and dragging will select more paragraphs.
  const scrollingContainer = findClosestScrollingContainer(quill.container);
  const draggingAutoScroll =
    scrollingContainer && new DraggingAutoScroll(scrollingContainer);
  draggingAutoScroll?.start();

  listenToMouseMoveUntilMouseUp({
    onMouseMove: (clientY: number) => {
      const hoveredLine = quill.lineFromYOffset(clientY);
      if (!hoveredLine) return;

      draggingAutoScroll?.setMouseYOffset(clientY);

      const hoveredLineIndex = hoveredLine.offset(hoveredLine.scroll);
      if (hoveredLineIndex <= start) {
        setSelectionWithoutScrolling(quill, {
          index: hoveredLineIndex,
          length: end - hoveredLineIndex,
        });
      } else {
        const length =
          hoveredLine instanceof BlockEmbed
            ? hoveredLine.length()
            : hoveredLine.length() - 1;
        setSelectionWithoutScrolling(quill, {
          index: start,
          length: hoveredLineIndex + length - start,
        });
      }
    },
    onMouseUp() {
      draggingAutoScroll?.stop();
    },
  });

  return true;
};

function listenToMouseMoveUntilMouseUp(options: {
  onMouseMove: (clientY: number) => void;
  onMouseUp: () => void;
}) {
  function handleMouseMove(mouseMoveEvent: MouseEvent) {
    // Mouse might be released outside of the browser window,
    // which won'trigger mouseup event, so we also check the mouse buttons state here.
    if (mouseMoveEvent.buttons === 0) {
      handleMouseUp();
      document.body.removeEventListener('mouseup', handleMouseUp);
    } else {
      options.onMouseMove(mouseMoveEvent.clientY);
    }
  }

  function handleMouseUp() {
    options.onMouseUp();
    document.body.removeEventListener('mousemove', handleMouseMove);
  }

  document.body.addEventListener('mousemove', handleMouseMove);
  document.body.addEventListener('mouseup', handleMouseUp, { once: true });
}

function setSelectionWithoutScrolling(quill: Quill, range: Range | null) {
  quill.selection.setRange(range, Quill.sources.USER);
}

export default selectParagraphsWithTripleClick;
